import { supabase, unwrap } from '@/services/supabase/client'
import type { CatSex, HomeSummary, MapPoint, NearbyCat, SightingResult } from '@/types/models'

export interface CreateSightingInput {
  /** Generated on the device before anything is sent, so a retry cannot double-register. */
  syncClientId: string
  catId?: string | null
  observedAt: string
  exactLat?: number | null
  exactLng?: number | null
  gpsAccuracy?: number | null
  newCatName?: string | null
  personalName?: string | null
  sex?: CatSex
  notes?: string | null
  city?: string | null
  region?: string | null
  country?: string | null
  note?: string | null
}

export async function createSighting(input: CreateSightingInput): Promise<SightingResult> {
  return unwrap(
    await supabase.rpc('create_sighting', {
      p_sync_client_id: input.syncClientId,
      p_cat_id: input.catId ?? null,
      p_observed_at: input.observedAt,
      p_exact_lat: input.exactLat ?? null,
      p_exact_lng: input.exactLng ?? null,
      p_gps_accuracy: input.gpsAccuracy ?? null,
      p_new_cat_name: input.newCatName ?? null,
      p_personal_name: input.personalName ?? null,
      p_sex: input.sex ?? 'unknown',
      p_notes: input.notes ?? null,
      p_city: input.city ?? null,
      p_region: input.region ?? null,
      p_country: input.country ?? null,
      p_note: input.note ?? null,
    }),
  ) as SightingResult
}

export async function linkSightingToCat(sightingId: string, catId: string): Promise<void> {
  unwrap(await supabase.rpc('link_sighting_to_cat', {
    p_sighting_id: sightingId,
    p_cat_id: catId,
  }))
}

export async function deleteSighting(sightingId: string): Promise<void> {
  unwrap(await supabase.from('sightings').delete().eq('id', sightingId))
}

export async function getHomeSummary(): Promise<HomeSummary> {
  return unwrap(await supabase.rpc('get_home_summary', {})) as HomeSummary
}

export async function getMapData(opts?: {
  ownerId?: string
  bounds?: { minLat: number; minLng: number; maxLat: number; maxLng: number }
  limit?: number
}): Promise<MapPoint[]> {
  return unwrap(
    await supabase.rpc('get_map_data', {
      p_owner: opts?.ownerId ?? null,
      p_min_lat: opts?.bounds?.minLat ?? null,
      p_min_lng: opts?.bounds?.minLng ?? null,
      p_max_lat: opts?.bounds?.maxLat ?? null,
      p_max_lng: opts?.bounds?.maxLng ?? null,
      p_limit: opts?.limit ?? 2000,
    }),
  ) as MapPoint[]
}

export async function getNearbyKnownCats(
  lat: number,
  lng: number,
  radiusM = 800,
  limit = 12,
  /** Registration lists only mutuals' cats; browsing shows everyone followed. */
  forRegistration = false,
): Promise<NearbyCat[]> {
  return unwrap(
    await supabase.rpc('get_nearby_known_cats', {
      p_lat: lat,
      p_lng: lng,
      p_radius_m: radiusM,
      p_limit: limit,
      p_for_registration: forRegistration,
    }),
  ) as NearbyCat[]
}

export interface MyCatByDistance {
  cat_id: string
  catalog_no: number
  name: string | null
  cover_media_id: string | null
  last_seen_at: string | null
  sighting_count: number
  metres: number | null
  last_place: string | null
  household: boolean
  friend_household: boolean
}

/** All of my cats, nearest first when a position is known. */
export async function getMyCatsByDistance(
  lat?: number | null,
  lng?: number | null,
  limit = 50,
): Promise<MyCatByDistance[]> {
  return unwrap(
    await supabase.rpc('get_my_cats_by_distance', {
      p_lat: lat ?? null,
      p_lng: lng ?? null,
      p_limit: limit,
    }),
  ) as MyCatByDistance[]
}
